import { LabelWithMessage } from '@common/LabelWithMessage';
import { FormTextareaWithMaxLength } from '@core/FormTextareaWithMaxLength';
import {
  FormCheckbox,
  FormSelect,
  FormTextField,
  requiredRule,
  FormFieldMethods,
  Grid,
  FormDatePicker,
} from '@gcl/react-core';
import { IRiskCategory } from '@riskCategory/riskCategory.models';
import * as React from 'react';
import { IVoItem } from '@voItem/voItem.models';
import { EMasterScheduleFormKey } from './masterScheduleDetail.models';

interface IMasterScheduleDetailDesignPackageProps {
  formFieldMethods: FormFieldMethods;
  isReadOnly: boolean;
  dataRiskCategories: IRiskCategory[] | undefined;
  dataVoItems: IVoItem[] | undefined;
}

export const MasterScheduleDetailDesignPackage: React.FunctionComponent<IMasterScheduleDetailDesignPackageProps> = ({
  formFieldMethods,
  isReadOnly,
  dataRiskCategories,
  dataVoItems,
}) => {
  const {
    formState: { isDirty },
  } = formFieldMethods;

  const riskCategory: IRiskCategory = formFieldMethods.watch(EMasterScheduleFormKey.RISK_CATEGORY);
  const workProcedureDrawingRequired = formFieldMethods.watch(EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS);
  const isWorkProcedureDrawingRequired = riskCategory?.category === 'A' || riskCategory?.category === 'B';

  React.useEffect(() => {
    if (isWorkProcedureDrawingRequired)
      if (!workProcedureDrawingRequired) {
        formFieldMethods.setError(EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS, {
          type: 'required',
          message: requiredRule.required.message,
        });
        return;
      }

    formFieldMethods.clearErrors(EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS);
  }, [riskCategory, workProcedureDrawingRequired]);

  return (
    <>
      {/* <Heading as={HeadingType.h3} styledAs={HeadingType.h5}>
        Design Package
      </Heading> */}
      <Grid container spacing={1.5} columnSpacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage
            testId={`t1-form-${EMasterScheduleFormKey.REF_NO}`}
            labelFor={EMasterScheduleFormKey.REF_NO}
            text="T1"
            message="Numbering system is set according to project. Please refer to your TWC or poject manager."
            required
          />
          <FormTextField
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.REF_NO}
            rules={requiredRule}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage
            labelFor={EMasterScheduleFormKey.DESIGN_PACKAGE}
            text="Design package"
            message="Give a short, clear title describing the Temporary Works."
            required
          />
          <FormTextField
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.DESIGN_PACKAGE}
            rules={requiredRule}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.RISK_CATEGORY} text="Risk category" required />
          <FormSelect
            options={dataRiskCategories || []}
            displayFieldName="category"
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.RISK_CATEGORY}
            rules={requiredRule}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.VO_ITEM} text="VO Item" />
          <FormSelect
            options={dataVoItems || []}
            displayFieldName="name"
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.VO_ITEM}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage
            labelFor={EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS}
            text="Require Work Procedure Drawing"
            message={
              isWorkProcedureDrawingRequired
                ? 'Works Procedure drawings are mandatory for Cat A and B design packages.'
                : undefined
            }
            required={isWorkProcedureDrawingRequired}
          />
          <FormCheckbox
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.REQUIRED_WORK_PROCEDURE_DRAWINGS}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.FALSEWORK} text="Falsework" />
          <FormCheckbox
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.FALSEWORK}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.PROCUREMENT_DATE} text="Procurement/Fabrication" />
          <FormDatePicker
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.PROCUREMENT_DATE}
            placeholder="Please input"
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.START_CONSTRUCTION_DATE} text="Start Construction" />
          <FormDatePicker
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.START_CONSTRUCTION_DATE}
            disabled={isReadOnly}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.REMOVAL_DATE} text="Removal/Redundant" />
          <FormDatePicker
            formFieldMethods={formFieldMethods}
            id={EMasterScheduleFormKey.REMOVAL_DATE}
            disabled={isReadOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <LabelWithMessage labelFor={EMasterScheduleFormKey.REMARKS} text="Remarks" />
          <FormTextareaWithMaxLength
            max={3000}
            id={EMasterScheduleFormKey.REMARKS}
            formFieldMethods={formFieldMethods}
            disabled={isReadOnly}
          />
        </Grid>
      </Grid>
    </>
  );
};
